import React from 'react';
import { DiamondIcon, OrderDataIcon, TaxonomyIcon } from 'assests/GmsIcons';
import { ArrowDown, ArrowUp, Sort } from 'Components/DesignSystem/Iconography/Icons';
import TableSkeletonElement from 'Components/GMS/Products/components/TableSkeletonElement';
import NoDataComponent from 'Components/Common/NoDataComponent';
import ReviewDropdown from 'Pages/GMS/Mapping/Components/ReviewDropdown';
import Toggle from 'Components/DesignSystem/Toggle/Toggle';
import {
    ATTRIBUTE_OVERLAP,
    CATEGORY,
    CATEGORY_TYPE,
    CO_SOLD_CATEGORY,
    CO_SOLD_CATEGORY_TYPE,
    INVOICE_COUNT,
    UNIFIED_SCORING,
    CURATED,
    REVIEW,
} from '../constants/CategoryRelevancyConstants';
import ThreeDotMenu from './ThreeDotMenu';

/* The glyphs are currentColor (okf/assests 2026-09-08); ink-secondary keeps the
   quiet weight the old #6D788C hardcoded. */
const DataSourceIcon = ({ dataSource }: { dataSource?: CoSoldCategory['data_source'] }) => {
    if (dataSource === 'unified') return <span className="tw-text-ink-secondary tw-inline-flex"><DiamondIcon /></span>;
    if (dataSource === 'order_data') return <span className="tw-text-ink-secondary tw-inline-flex"><OrderDataIcon /></span>;
    if (dataSource === 'taxonomy') return <span className="tw-text-ink-secondary tw-inline-flex"><TaxonomyIcon /></span>;
    return null;
};

type Top25TableProps = {
    rows: CoSoldCategoryParent[];
    loading: boolean;
    rowDensity: RowDensity;
    showAttributeOverlap: boolean;
    showUnifiedScoring: boolean;
    showTaxonomyIcon: boolean;
    onToggleCuration: (coSoldCategoryId: string, parentCategoryId: string, isCurated: boolean, reviewStatus: CoSoldCategory['review_status']) => void;
    onUpdateReviewStatus: (coSoldCategoryId: string, parentCategoryId: string) => void;
    handleSort: (column: SortOption) => void;
    sortBy: SortOption;
    sortOrder: SortOrder;
    disabled?: boolean;
    onOpenCreateCompatibility: (categoryA: { id: string; name: string }, categoryB: { id: string; name: string }) => void;
    onOpenViewCompatibility: (categoryA: { id: string; name: string }, categoryB: { id: string; name: string }) => void;
};

const Top25Table: React.FC<Top25TableProps> = ({
    rows,
    loading,
    rowDensity,
    showAttributeOverlap,
    showUnifiedScoring,
    showTaxonomyIcon,
    onToggleCuration,
    onUpdateReviewStatus,
    handleSort,
    sortBy,
    sortOrder,
    disabled = false,
    onOpenCreateCompatibility,
    onOpenViewCompatibility,
}) => {

    const getRowPadding = () => {
        switch (rowDensity) {
            case 'compact': return 'tw-py-1';
            case 'comfortable': return 'tw-py-3';
            default: return 'tw-py-2';
        }
    };
    const rowPadding = getRowPadding();
    const headerClasses = "tw-cursor-pointer tw-py-3 tw-px-3 tw-text-left tw-text-sm tw-font-semibold tw-text-ink-secondary tw-tracking-wider tw-align-middle";
    const truncatedCell = "tw-truncate tw-max-w-0 tw-overflow-hidden tw-whitespace-nowrap";

    // Flatten CoSoldCategoryParent[] into individual rows with parent info embedded
    const flatRows = rows.flatMap(parent =>
        (parent.co_sold_categories ?? []).map(cs => ({ ...cs, parentCategoryId: parent.category_id, parentCategoryName: parent.category_name, parentCategoryTypeName: parent.category_type_name }))
    );

    const getColumnWidth = (column: SortOption | 'icon' | 'review'): string => {
        switch (column) {
            case 'icon': return 'tw-w-[40px]';
            case 'invoice_count': return 'tw-w-[140px]';
            case 'attribute_overlap': return 'tw-w-[150px]';
            case 'unified_score': return 'tw-w-[140px]';
            case 'is_curated': return 'tw-w-[100px]';
            case 'review': return 'tw-w-[160px]';
            default: return '';
        }
    };

    /* The DS sort glyph trio — the same three TableHeader draws (currentColor, so
       they inherit the header's ink-secondary). */
    const sortElement = (column: string) => {
        return sortBy === column ? (
            <span className="tw-ml-1 tw-inline-flex">
                {sortOrder === 1 ? (
                    <ArrowUp />
                ) : (
                    <ArrowDown />
                )}
            </span>
        ) : (
            <Sort />
        );
    };

    const renderHeaderCell = (
        column: SortOption,
        label: string,
        indent: boolean = false,
        sortable: boolean = true
    ) => (
        <th
            key={column}
            className={`${headerClasses} ${getColumnWidth(column)}`}
            onClick={sortable ? () => handleSort(column) : undefined}
            style={sortable ? undefined : { cursor: 'default' }}
        >
            <div className={`tw-flex tw-items-center ${indent ? 'tw-ml-7' : ''}`}>
                <span>{label}</span>
                {sortable && <span className="tw-ml-1">{sortElement(column)}</span>}
            </div>
        </th>
    );

    const formatValue = (value: number | null | undefined): string => {
        if (value === null || value === undefined) return '-';
        return value.toString();
    };

    const extraCols =
        (showTaxonomyIcon ? 1 : 0) +
        (showAttributeOverlap ? 1 : 0) +
        (showUnifiedScoring ? 1 : 0);
    // base: category, cat type, co-sold cat, co-sold cat type, invoice, curated, review, actions = 8
    const totalColSpan = 8 + extraCols;

    // Dynamic min-width: 4 flexible columns × 150px + all fixed-width columns
    const fixedColsWidth =
        (showTaxonomyIcon ? 40 : 0) +
        140 + // invoice_count
        (showAttributeOverlap ? 150 : 0) +
        (showUnifiedScoring ? 140 : 0) +
        100 + // curated
        160 + // review
        48;   // actions
    const minTableWidth = 600 + fixedColsWidth;

    if (loading) return <div><TableSkeletonElement /></div>;

    return (
        <div className="tw-border tw-border-solid tw-rounded-md tw-mt-3 tw-border-edge tw-overflow-hidden tw-mb-4">
            <div className="table-container" style={{ width: '100%', height: 'auto', overflowX: 'auto' }}>
                {/* tw-text-ink on the table: cell text must never ride UA inheritance —
                    the dark body rule sets background only (CBM § 4.6 lesson). */}
                <table className="tw-bg-surface tw-text-ink tw-w-full tw-table-fixed" style={{ minWidth: minTableWidth }}>
                    <thead className="tw-sticky tw-top-0 tw-bg-surface-sunken">
                        <tr>
                            {showTaxonomyIcon && (
                                <th className={`${headerClasses} ${getColumnWidth('icon')} tw-cursor-default`} />
                            )}
                            {renderHeaderCell('category_name', CATEGORY, !showTaxonomyIcon)}
                            {renderHeaderCell('category_type_name', CATEGORY_TYPE)}
                            {renderHeaderCell('co_sold_category', CO_SOLD_CATEGORY)}
                            {renderHeaderCell('co_sold_category_type', CO_SOLD_CATEGORY_TYPE)}
                            {renderHeaderCell('invoice_count', INVOICE_COUNT)}
                            {showAttributeOverlap && renderHeaderCell('attribute_overlap', ATTRIBUTE_OVERLAP)}
                            {showUnifiedScoring && renderHeaderCell('unified_score', UNIFIED_SCORING)}
                            {renderHeaderCell('is_curated', CURATED)}
                            <th className={`${headerClasses} ${getColumnWidth('review')} tw-cursor-default`}>
                                <div className="tw-flex tw-items-center">
                                    <span>{REVIEW}</span>
                                </div>
                            </th>
                            <th className="tw-w-[48px]" />
                        </tr>
                    </thead>
                    <tbody>
                        {flatRows.length === 0 ? (
                            <tr>
                                <td colSpan={totalColSpan} className="tw-bg-surface"><NoDataComponent title="No Categories Found" /></td>
                            </tr>
                        ) : (
                            flatRows.map((row, index) => {
                                const rowBg = index % 2 === 0 ? 'tw-bg-surface' : 'tw-bg-surface-sunken';
                                return (
                                    <tr key={`${row.parentCategoryId}-${row.category_id}-${index}`} className="hover:tw-bg-surface-hover">
                                        {showTaxonomyIcon && (
                                            <td className={`${rowPadding} ${rowBg} tw-px-2 tw-text-center`}>
                                                <DataSourceIcon dataSource={row.data_source} />
                                            </td>
                                        )}
                                        <td className={`${rowPadding} ${truncatedCell} ${rowBg} tw-pl-4 tw-pr-3`} title={row.parentCategoryName}>
                                            {row.parentCategoryName}
                                        </td>
                                        <td className={`${rowPadding} ${truncatedCell} ${rowBg} tw-px-3`} title={row.parentCategoryTypeName}>
                                            {row.parentCategoryTypeName}
                                        </td>
                                        <td className={`${rowPadding} ${truncatedCell} ${rowBg} tw-px-3`} title={row.category_name}>
                                            {row.category_name}
                                        </td>
                                        <td className={`${rowPadding} ${truncatedCell} ${rowBg} tw-px-3`} title={row.category_type_name}>
                                            {row.category_type_name}
                                        </td>
                                        <td className={`${rowPadding} ${truncatedCell} ${rowBg} tw-px-3`}>
                                            {row.invoice_count}
                                        </td>
                                        {showAttributeOverlap && (
                                            <td className={`${rowPadding} ${truncatedCell} ${rowBg} tw-px-3 tw-text-sm`}>
                                                {formatValue(row.attribute_overlap)}
                                            </td>
                                        )}
                                        {showUnifiedScoring && (
                                            <td className={`${rowPadding} ${truncatedCell} ${rowBg} tw-px-3 tw-text-sm`}>
                                                {formatValue(row.unified_score)}
                                            </td>
                                        )}
                                        <td
                                            data-testid={`components-top25table-td-1-${index}`}
                                            className={`${rowPadding} ${rowBg}`}
                                            onClick={(e) => e.stopPropagation()}
                                            // A pair must be Reviewed before it can be curated; an already-curated
                                            // pair stays togglable so it can still be un-curated.
                                            title={!row.is_curated && row.review_status !== 'Reviewed' ? 'Mark as Reviewed before curating' : undefined}
                                        >
                                            <Toggle
                                                checked={!!row.is_curated}
                                                onChange={(val) => onToggleCuration(row.category_id, row.parentCategoryId, val, row.review_status)}
                                                label=""
                                                disabled={disabled || (!row.is_curated && row.review_status !== 'Reviewed')}
                                            />
                                        </td>
                                        <td className={`${rowPadding} ${rowBg}`}>
                                            <ReviewDropdown
                                                value={row.review_status === 'Reviewed'}
                                                onChange={(value) => { if (value) onUpdateReviewStatus(row.category_id, row.parentCategoryId); }}
                                                disabled={disabled || row.review_status === 'Reviewed'}
                                                itemId={row.category_id}
                                            />
                                        </td>
                                        <td className={`${rowPadding} ${rowBg} tw-text-center`}>
                                            <ThreeDotMenu
                                                disabled={disabled}
                                                onCreateCompatibility={() => onOpenCreateCompatibility(
                                                    { id: row.parentCategoryId, name: row.parentCategoryName },
                                                    { id: row.category_id, name: row.category_name }
                                                )}
                                                onViewCompatibility={() => onOpenViewCompatibility(
                                                    { id: row.parentCategoryId, name: row.parentCategoryName },
                                                    { id: row.category_id, name: row.category_name }
                                                )}
                                            />
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default Top25Table;
